import {Component} from '@packages/systems/wfdl';

import {
  ConsolidatedBindingContext,
  getElementBindingContext,
} from '@packages/systems/dynamo/gateway';
import {
  closestDynamoWrapperElement,
  isMultiReferenceFieldConnectedDynamoWrapperElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {isComparisonOperator} from '@packages/systems/dynamo/utils/DynQueryUtils';

import {
  getItemRefSlug,
  isEmptyFieldPath,
} from '@packages/systems/dynamo/utils/ParamFieldPathUtils';

import {
  getTypeDependentElements,
  findBindingSourceByFieldPath,
  getSingularLabel,
  getBindingSource,
  convertItemRefFilterOperatorForPersisting,
} from '@packages/systems/dynamo/utils/ParamPanelUtils';

import {isValueNeeded} from './utils';

import {getFieldOptions} from './getFieldOptions';
import {getItemRefOptions} from './getItemRefOptions';
import {getOptionOptions} from './getOptionOptions';
import {getBindingSourceLabel} from './getFieldLabel';
import {getSelectedItemRefOptionLabel} from './getSelectedItemRefOptionLabel';

import {fieldChangeHandler} from './fieldChangeHandler';

import {getOperatorOptions, operatorChangeHandler} from './shared';

import type {CollectionsType} from '@packages/systems/core/records/CollectionStateType';

import type {
  ParamPanelState,
  ParamPanelProps,
  ParamPanelUIProps,
} from './types';
import {selectCurrentLocale} from '@packages/systems/localization';

// Base type containing common properties shared across all normalizer props
type BaseNormalizerProps = {
  type: ParamPanelProps['type'];
  stores: ParamPanelProps['stores'];
  fieldPath: ParamPanelState['fieldPath'];
  operator: ParamPanelState['operator'];
  value: ParamPanelState['value'];
  value2: ParamPanelState['value2'];
  value3: ParamPanelState['value3'];
  onChange: (arg1: Partial<ParamPanelState>) => void;
  onValueChange: ParamPanelUIProps['onValueChange'];
  onValue2Change: ParamPanelUIProps['onValue2Change'];
  onValue3Change: ParamPanelUIProps['onValue3Change'];
};

// Props specific to component-based normalization
type ElementNormalizerProps = {
  currentComponent: ParamPanelProps['currentComponent'];
  componentMap: ParamPanelProps['componentMap'];
  currentPage: ParamPanelProps['currentPage'];
  selectedNodeNativeId: ParamPanelProps['selectedNodeNativeId'];
};

// Props specific to binding context-based normalization
type BindingContextSpecificProps = {
  bindingContext: ConsolidatedBindingContext | null;
  parentBindingContext: ConsolidatedBindingContext | null;
};

type NormalizeCommonPropsConfig = BaseNormalizerProps &
  BindingContextSpecificProps & {
    selectedNodeNativeId: ParamPanelProps['selectedNodeNativeId'];
    canHaveCorruptContextualValue?: boolean;
    insideItemRefSetList?: boolean;
  };

type NormalizerProps = BaseNormalizerProps & ElementNormalizerProps;
type NormalizerPropsWithBindingContexts = BaseNormalizerProps &
  BindingContextSpecificProps;

// TODO (dynamo infra): Memoize this to prevent referential equality breaking
export function normalizePropsForRendering(
  props: NormalizerProps
): ParamPanelUIProps {
  const {
    type,
    stores,
    stores: {collection: collectionState},
    currentComponent,
    componentMap,
    currentPage,
    selectedNodeNativeId,
    ...commonProps
  } = props;

  const {collections} = collectionState;

  const {element, dynamoItem, dynamoWrapper} = getTypeDependentElements({
    type,
    selectedNodeNativeId,
    currentComponent,
  });

  /*
    Collection list filters are essentially DynamoWrapper settings, but
    they are visible and configurable on DynamoWrapper, DynamoList,
    DynamoItem and Pagination elements.

    `getElementBindingContext` finds a collection list's binding context
    always from its DynamoItem element, which means we need to pass the
    DynamoItem of the closest DynamoWrapper to find the binding context
    that should be used for configuring collection list filters.

    For conditional visibility configuration, we can always pass the
    selected element to `getElementBindingContext`.
  */
  const contextElement = type === 'query' ? dynamoItem : element;

  const bindingContext = contextElement
    ? getElementBindingContext(
        contextElement.id,
        currentComponent,
        componentMap,
        currentPage,
        collections
      )
    : null;

  const parentBindingContext =
    bindingContext && dynamoWrapper
      ? getElementBindingContext(
          dynamoWrapper.id,
          currentComponent,
          componentMap,
          currentPage,
          collections
        )
      : null;

  const insideItemRefSetList = Boolean(
    dynamoWrapper &&
    isMultiReferenceFieldConnectedDynamoWrapperElement(dynamoWrapper)
  );

  const directlyClosestDynamoWrapper = element
    ? closestDynamoWrapperElement(
        Component.getRender(currentComponent),
        element
      )
    : null;

  /*
    An element that is inside a DynamoWrapper but not inside a DynamoItem can
    have a corrupt DYN_CONTEXT or DYN_CONTEXT.fieldSlug value if the selected
    field option is a reference field.

    For example, those value options were available for elements inside a
    DynamoEmpty with a binding context (like on a collection template page).
  */
  const canHaveCorruptContextualValue = Boolean(
    // this only applies to conditional visibility
    type === 'condition' &&
    directlyClosestDynamoWrapper &&
    (!dynamoItem || directlyClosestDynamoWrapper !== dynamoWrapper)
  );

  return normalizeCommonProps({
    ...commonProps,
    type,
    stores,
    bindingContext,
    parentBindingContext,
    canHaveCorruptContextualValue,
    insideItemRefSetList,
    selectedNodeNativeId,
  });
}

export function normalizePropsForRenderingWithBindingContexts(
  props: NormalizerPropsWithBindingContexts
): ParamPanelUIProps {
  const {bindingContext, parentBindingContext, ...commonProps} = props;

  return normalizeCommonProps({
    ...commonProps,
    bindingContext,
    parentBindingContext,
    canHaveCorruptContextualValue: false,
    insideItemRefSetList: false,
    selectedNodeNativeId: null,
  });
}

function normalizeCommonProps({
  type,
  stores,
  bindingContext,
  parentBindingContext,
  fieldPath,
  operator,
  value,
  value2,
  value3,
  onChange,
  onValueChange,
  onValue2Change,
  onValue3Change,
  canHaveCorruptContextualValue = false,
  insideItemRefSetList = false,
  selectedNodeNativeId,
}: NormalizeCommonPropsConfig): ParamPanelUIProps {
  const {collection: collectionState, localizationStore} = stores;
  const bindingSources = bindingContext
    ? bindingContext.getExpandedBindingSources()
    : [];

  const bindingSource = bindingContext
    ? getBindingSource(fieldPath, bindingContext)
    : null;

  const fieldType = bindingSource ? bindingSource.type : '';
  const fieldInnerType =
    bindingSource && bindingSource.type === 'Set'
      ? bindingSource.meta.innerType
      : '';

  const itemRefFieldSlug = getItemRefSlug(fieldPath);
  const contextBindingSource = itemRefFieldSlug
    ? findBindingSourceByFieldPath(bindingSources, itemRefFieldSlug)
    : null;

  const availableFieldOptions = bindingContext
    ? getFieldOptions(
        type,
        bindingContext,
        // "Current Item" field option should not be available inside
        // a multi-reference field connected collection list, because
        // self-references are not allowed.
        insideItemRefSetList ? null : parentBindingContext
      )
    : [];

  const availableOperators = getOperatorOptions(
    fieldPath,
    fieldType,
    fieldInnerType
  );

  const availableItemRefOptions = bindingSource
    ? getItemRefOptions({
        bindingSource,
        parentBindingContext,
        collectionState,
        currentLocale: selectCurrentLocale(localizationStore),
      })
    : [];

  const availableOptionOptions = bindingSource
    ? getOptionOptions(bindingSource)
    : [];

  const onFieldChange = fieldChangeHandler({
    handleChange: onChange,
    bindingContext,
    parentBindingContext,
    operator,
    collectionState,
    currentLocale: selectCurrentLocale(localizationStore),
  });

  const onOperatorChange = operatorChangeHandler({
    handleChange: onChange,
    operator,
    fieldType,
    availableItemRefOptions,
    availableOptionOptions,
  });

  const itemRefOptionLabel = getSelectedItemRefOptionLabel({
    fieldType,
    value,
    /*
      Attempt to account for corrupt DYN_CONTEXT values so that the label is
      rendered correctly even if DYN_CONTEXT values are no longer available.

      First, we try to provide an actual parent binding context from Dynamo
      Gateway, because an element inside a DynamoEmpty of a nested collection
      list could have legitimate DYN_CONTEXT value options.

      Second, if there is no parent binding context, we try to pass in the
      actual binding context, because it would have been the base for potential
      corrupt values.
    */
    bindingContext: canHaveCorruptContextualValue
      ? parentBindingContext || bindingContext
      : parentBindingContext,
    availableItemRefOptions,
  });

  return {
    availableFieldOptions,
    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'OperatorId'.
    availableOperators,
    availableItemRefOptions,
    availableOptionOptions,
    bindingSource,

    collectionName: bindingContext ? getSingularLabel(bindingContext) : '',
    fieldLabel: getBindingSourceLabel(bindingSource, contextBindingSource),
    fieldType,
    itemRefOptionLabel,
    selectedNodeNativeId,

    fieldPath,
    operator,
    value,
    value2,
    value3,
    stores,

    onFieldChange,
    onOperatorChange,
    onValueChange,
    onValue2Change,
    onValue3Change,
  };
}

type NormalizedStateOrError =
  | Readonly<
      {
        error: false;
        fieldType: string;
      } & ParamPanelState
    >
  | Readonly<{
      error: true;
      message: string;
    }>;

export function normalizeStateForPersisting({
  state,
  type,
  selectedNodeNativeId,
  currentComponent,
  componentMap,
  currentPage,
  collections,
}: {
  state: ParamPanelState;
  type: ParamPanelProps['type'];
  selectedNodeNativeId: string;
  currentComponent: ParamPanelProps['currentComponent'];
  componentMap: ParamPanelProps['componentMap'];
  currentPage: ParamPanelProps['currentPage'];
  collections: CollectionsType;
}): NormalizedStateOrError {
  const {fieldPath, operator, value, value2, value3} = state;

  const {element, dynamoItem, dynamoWrapper} = getTypeDependentElements({
    type,
    selectedNodeNativeId,
    currentComponent,
  });

  /*
    Collection list filters are essentially DynamoWrapper settings, but
    they are visible and configurable on DynamoWrapper, DynamoList,
    DynamoItem and Pagination elements.

    `getElementBindingContext` finds a collection list's binding context
    always from its DynamoItem element, which means we need to pass the
    DynamoItem of the closest DynamoWrapper to find the binding context
    that should be used for configuring collection list filters.

    For conditional visibility configuration, we can always pass the
    selected element to `getElementBindingContext`.
  */
  const contextElement = type === 'query' ? dynamoItem : element;

  const bindingContext = contextElement
    ? getElementBindingContext(
        contextElement.id,
        currentComponent,
        componentMap,
        currentPage,
        collections
      )
    : null;

  const bindingSource = bindingContext
    ? getBindingSource(fieldPath, bindingContext)
    : null;

  const fieldType = bindingSource ? bindingSource.type : '';

  const errorMessage = validateState(state, fieldType);

  if (errorMessage) {
    return {error: true, message: errorMessage};
  }

  if (bindingSource && bindingSource.type === 'ItemRef') {
    const parentBindingContext =
      bindingContext && dynamoWrapper
        ? getElementBindingContext(
            dynamoWrapper.id,
            currentComponent,
            componentMap,
            currentPage,
            collections
          )
        : null;

    return {
      error: false,
      fieldType,
      fieldPath,
      operator: convertItemRefFilterOperatorForPersisting(
        operator,
        value,
        parentBindingContext
      ),
      value,
      value2,
      value3,
    };
  }

  return {
    error: false,
    fieldType,
    fieldPath,
    operator,
    value,
    value2,
    value3,
  };
}

function validateState(
  state: ParamPanelState,
  fieldType: string
): string | null {
  const {fieldPath, operator, value, value2, value3} = state;
  const needsValue = isValueNeeded(fieldType, operator);
  const needsValue2 = fieldType === 'Date' && isComparisonOperator(operator);
  const needsValue3 = fieldType === 'Date' && isComparisonOperator(operator);

  // @ts-expect-error - TS7006 - Parameter 'v' implicitly has an 'any' type.
  const _isSetValue = (v) => {
    if (typeof v === 'string') {
      return Boolean(v);
    } else {
      return v != null;
    }
  };

  if (isEmptyFieldPath(fieldPath)) {
    return 'Specify a field for this criteria';
  }

  if (!operator) {
    return 'Specify an operator for criteria';
  }

  if (
    (!_isSetValue(value) && needsValue) ||
    (!_isSetValue(value2) && needsValue2) ||
    (!_isSetValue(value3) && needsValue3)
  ) {
    return 'Specify a valid value';
  }

  return null;
}
